<template>
  <LModal
    :visible="modelValue"
    :title="compoundTitle"
    :size="isSpecialAdmin() ? 'xxl' : 'lg'"
    auto-focus-button="ok"
    ok-variant="success"
    :submit="submitStamp"
    @update:visible="handleVisibleUpdate"
    @cancel="stopTimer"
  >
    <template #default="{ submit }">
      <TabParent
        v-model="activeTab"
        :class="{ 'single-tab': data.state === TCStampState.STAMPED_OUT }"
      >
        <TabChild
          :title="
            t(
              data.state === TCStampState.STAMPED_IN
                ? 'components.rfid.fields.stampOut'
                : 'components.rfid.fields.stampIn',
            )
          "
          value="stamp"
          ignore-validation
        >
          <div class="text-center bold">
            <span v-if="data.state === TCStampState.STAMPED_OUT">
              {{ t("components.rfid.doYouWantToStampIn") }}
            </span>
            <span v-else>{{ t("components.rfid.doYouWantToStampOut") }}</span>
          </div>
          <div v-if="data.state === TCStampState.STAMPED_IN">
            <hr />
            <span class="font-bold">{{ t("components.rfid.header.breakTimeLabel") }}</span>
            <div class="grid">
              <div class="col-3">
                <div class="flex flex-column">
                  <LISAButton
                    v-for="time in breakTimeMutations"
                    :key="'timeButton' + time"
                    class="text-left text-overflow-ellipsis"
                    @click="mutateBreakTime('minus', time)"
                  >
                    - {{ t("views.rfid.durationInMinutesShort", { time }) }}
                  </LISAButton>
                </div>
              </div>
              <div class="text-center col">
                <Calendar
                  v-model="breakTimeDate"
                  time-only
                  inline
                  @update:model-value="resetTimer"
                />
              </div>
              <div class="text-right col-3">
                <div class="flex flex-column">
                  <LISAButton
                    v-for="time in breakTimeMutations"
                    :key="'add' + time"
                    class="text-left text-overflow-ellipsis"
                    @click="mutateBreakTime('plus', time)"
                  >
                    + {{ t("views.rfid.durationInMinutesShort", { time }) }}
                  </LISAButton>
                </div>
              </div>
            </div>
          </div>
          <InputRow
            v-model="comment"
            :name="commentTagName"
            :display-name="t('license.timeStampClock.Stamp.comment')"
          >
            <template #default="{ field: inputField }">
              <PrimeVueTextarea
                v-bind="inputField"
                v-model="comment"
                class="w-100"
                rows="8"
                @keydown.meta.enter="submit"
                @keydown.ctrl.enter="submit"
                @keyup="resetTimer"
              />
            </template>
          </InputRow>
          <hr />
          <template v-if="data.stampData">
            <span class="font-bold">{{ t("components.rfid.header.lastStamps") }}</span>
            <table class="w-100">
              <thead>
                <tr>
                  <th>{{ t("components.rfid.fields.stampedIn") }}</th>
                  <th>{{ t("components.rfid.fields.stampedOut") }}</th>
                  <th>{{ t("components.rfid.fields.breakTime") }}</th>
                  <th>{{ t("components.rfid.fields.duration") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(stamp, index) in stampData" :key="`Stamp${index}`">
                  <td
                    :class="{
                      'to-create-stamp': !stamp.timestampOut && index + 1 === stampData.length,
                    }"
                  >
                    {{ formatDate(stamp.timestampIn) }}
                  </td>
                  <td :class="{ 'to-create-stamp': index + 1 === stampData.length }">
                    <span v-if="stamp.timestampOut">{{ formatDate(stamp.timestampOut) }}</span>
                  </td>
                  <td
                    :class="{ 'to-create-stamp': index + 1 === stampData.length }"
                    class="text-right"
                  >
                    {{ stamp.break == null ? "-" : formatDuration(stamp.break, false, true) }}
                  </td>
                  <td
                    :class="{ 'to-create-stamp': index + 1 === stampData.length }"
                    class="text-right"
                  >
                    {{ formatDuration(stamp.total ?? null, false, true) }}
                  </td>
                </tr>
                <tr class="footer">
                  <td></td>
                  <td>{{ t("components.rfid.fields.total") }}</td>
                  <td class="text-right">
                    {{ formatDuration(cumulatedTimes.break, false, true) }}
                  </td>
                  <td class="text-right">
                    {{ formatDuration(cumulatedTimes.work, false, true) }}
                  </td>
                </tr>
                <tr class="footer">
                  <td></td>
                  <td>{{ t("components.rfid.fields.timeShould") }}</td>
                  <td></td>
                  <td class="text-right">
                    {{ formatDuration(cumulatedTimes.workTimeShould, false, true) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </template>
          <hr />
        </TabChild>
        <TabChild
          v-if="data.state === TCStampState.STAMPED_IN && isSpecialAdmin()"
          :title="t('global.actions')"
          value="changedActions"
          ignore-validation
        >
          <RFIDChangedActions v-if="changedActionsOpened" :login="login" />
        </TabChild>
      </TabParent>
    </template>
    <template #modal-ok>
      <i class="fas fa-lock mr-1" />
      <span v-if="data.state === TCStampState.STAMPED_OUT">{{
        t("components.rfid.fields.stampIn")
      }}</span>
      <span v-else>{{ t("components.rfid.fields.stampOut") }}</span>
    </template>

    <template #modal-cancel>
      <i class="fas fa-times mr-1"></i>
      {{ t("global.cancel") }} (
      {{ countdownTime }}
      )
    </template>
  </LModal>
</template>

<script setup lang="ts">
import { ref, onBeforeUnmount, onMounted, computed, watch } from "vue";
import { DateTime, Duration } from "luxon";
import { useTranslation } from "@/language/translation";
import LISAButton from "@LISAPrime/LISAButton.vue";
import { useToast } from "primevue/usetoast";
import PrimeVueTextarea from "primevue/textarea";
import Calendar from "primevue/calendar";
import {
  type TCGetStampStateForLoginQuery,
  type TCGetStampStateForRfidQuery,
  useTCStampUserWithLoginMutation,
  useTCStampUserWithRfidMutation,
  TCStampState,
} from "@/graphql";
import LModal from "../LModal.vue";
import { LISA_DATE_FULL } from "@/language/translation";
import { type CombinedLoginPayload } from "@/license/timeStampClock/helper/TCTypes";
import { formatDuration } from "@/license/timeStampClock/helper/FormatDuration";
import InputRow from "@/components/shared/form/InputRow.vue";
import TabParent from "@/components/shared/utility/Tabs/TabParent.vue";
import TabChild from "@/components/shared/utility/Tabs/TabChild.vue";
import RFIDChangedActions from "./RFIDChangedActions.vue";
import { isSpecialAdmin } from "@/functions/environment";

const defaultTimerTimeoutTime = 30;

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  data: {
    type: Object as () =>
      | TCGetStampStateForRfidQuery["TCStampPreacquisitionData"]
      | TCGetStampStateForLoginQuery["TCStampPreacquisitionData"],
    required: true,
  },
  login: {
    type: Object as () => CombinedLoginPayload,
    required: true,
  },
});
const emit = defineEmits(["update:modelValue"]);
const toast = useToast();
const { t } = useTranslation();
const compoundTitle = computed(
  () => `${t("components.rfid.header.greeting")} ${props.data.user.preferredName}`,
);
const breakTimeMutations = ref([15, 30, 60]);
const breakTime = ref("00:00:00");
const activeTab = ref("stamp");
const changedActionsOpened = ref(false);

watch(activeTab, (tab) => {
  if (tab === "changedActions") {
    changedActionsOpened.value = true;
  }
  resetTimer();
});

const countDownRerenderHack = ref(0);

const cumulatedTimes = computed(() => ({
  break: props.data.stampData
    ?.flatMap((data) => data.stamps ?? [])
    .map((el) => el.break ?? 0)
    .reduce((lhs, rhs) => lhs + rhs, 0),
  work: props.data.stampData
    ?.flatMap((data) => data.stamps ?? [])
    .map((el) => el.total ?? 0)
    .reduce((lhs, rhs) => lhs + rhs, 0),
  workTimeShould: props.data.stampData?.reduce((lhs, rhs) => lhs + (rhs.workingTime ?? 0), 0) ?? 0,
}));

const { mutate: stampMutation } = useTCStampUserWithRfidMutation({});
const { mutate: stampLoginMutation } = useTCStampUserWithLoginMutation({});

const comment = ref("");

const stampData = computed(
  () =>
    props.data.stampData
      ?.toSorted((a, b) => Number(a.day < b.day))
      .flatMap((data) => data.stamps ?? [])
      ?.sort((a, b) => a.timestampIn.localeCompare(b.timestampIn)) ?? [],
);

async function stampIn() {
  if ("code" in props.login) {
    await stampMutation({
      code: props.login.code,
      additions: {
        comment: comment.value,
        break: props.data.state === TCStampState.STAMPED_IN ? breakTime.value : undefined,
      },
    });
  }
  if ("username" in props.login) {
    await stampLoginMutation({
      username: props.login.username,
      password: props.login.password,
      additions: {
        comment: comment.value,
        break: props.data.state === TCStampState.STAMPED_IN ? breakTime.value : undefined,
      },
    });
  }
  toast.add({
    severity: "success",
    summary: t("global.information"),
    detail:
      props.data.state === TCStampState.STAMPED_OUT
        ? t("components.rfid.fields.stampedIn")
        : t("components.rfid.fields.stampedOut"),
    life: 5000,
  });
}

async function submitStamp() {
  try {
    await stampIn();
    return { success: true };
  } catch (error) {
    close();
    throw error;
  }
}

function close() {
  stopTimer();
  emit("update:modelValue", false);
}

function handleVisibleUpdate(visible: boolean) {
  if (!visible) {
    close();
  }
}

const countdownTime = ref(defaultTimerTimeoutTime);
const timeout = ref<number>();

function stopTimer() {
  if (timeout.value == null) {
    return;
  }
  clearInterval(timeout.value);
  timeout.value = undefined;
}

function resetTimer() {
  countDownRerenderHack.value += 1;
  stopTimer();
  countdownTime.value = defaultTimerTimeoutTime;
  timeout.value = setInterval(() => {
    countdownTime.value -= 1;
    if (countdownTime.value <= 0) {
      close();
    }
  }, 1000);
}

function formatDate(date?: string) {
  if (date == null) {
    return "";
  }
  return DateTime.fromISO(date).toLocaleString(LISA_DATE_FULL);
}

const mutateBreakTime = (mod: "plus" | "minus", amount: number) => {
  const time = Duration.fromISOTime(breakTime.value)[mod]({ minutes: amount });
  if (time.valueOf() < 0) {
    breakTime.value = "00:00:00";
  } else {
    breakTime.value = time.toFormat("hh:mm:ss");
  }
  resetTimer();
};

const breakTimeDate = computed({
  get() {
    return DateTime.fromFormat(breakTime.value, "HH:mm:ss").toJSDate();
  },
  set(val: Date) {
    breakTime.value = DateTime.fromJSDate(val).toFormat("HH:mm:ss");
  },
});

onMounted(() => {
  resetTimer();
});

onBeforeUnmount(() => {
  stopTimer();
});

const commentTagName = crypto.randomUUID();
</script>

<style lang="scss" scoped>
.footer {
  background-color: rgba(122, 122, 122, 0.1);
  font-weight: bold;
}

th {
  vertical-align: top !important;
  background: var(--p-primary-color) !important;
  color: var(--p-primary-contrast-color) !important;
}
td {
  padding: 8px;
}
tr:nth-child(even) {
  background-color: var(--p-surface-100);
}

.to-create-stamp {
  color: var(--p-info-color);
}

.single-tab :deep(.p-tablist) {
  display: none;
}
</style>
